import * as React from 'react';

import { cn } from '@mds/shared/lib/utils';

export type StatementSwipeItem = {
  /** The display word — "Vision". Also the faint word behind the copy. */
  title: string;
  /** The statement itself, one or two sentences. */
  body: string;
  /** Optional: without one the card keeps the photo's height, on its own ground. */
  image?: { src: string; alt: string };
};

export type StatementSwipeProps = {
  items: StatementSwipeItem[];
  /** Names the set for a screen reader, since the card has no visible title. */
  label: string;
  className?: string;
};

/** How far a finger has to travel sideways before it counts as a swipe. */
const SWIPE_DISTANCE = 40;

/**
 * Statements shown one at a time on a single card: a photograph over the
 * statement, a faint copy of its word behind the copy, and a bar per
 * statement along the foot. Swipe the card, or tap a bar, to change it.
 *
 * Built for the About page's Vision and Mission on a phone, where the two side
 * by side ran to a screen and a half. Nothing else is written on the card — no
 * counter, no caption, no number — because the bars already say there is more.
 *
 * Every statement is drawn into the same grid cell and all but the chosen one
 * hidden, so the card is as tall as the longest and does not jump as it
 * changes. Hidden with `invisible`, which keeps the box but takes it out of
 * the accessibility tree as well as out of sight.
 */
export default function StatementSwipe({ items, label, className }: StatementSwipeProps) {
  const [active, setActive] = React.useState(0);
  const startX = React.useRef<number | null>(null);

  if (items.length === 0) return null;

  const go = (index: number) => setActive(Math.min(Math.max(index, 0), items.length - 1));

  return (
    <section
      aria-label={label}
      aria-roledescription="carousel"
      // One charcoal card for every statement: a swipe changes the words and
      // the picture, not the ground. `data-ground` re-points the text roles to
      // the steps that read on it.
      data-ground="panel"
      // `touch-pan-y` keeps the page scrolling under a finger that moves up
      // and down, and hands a sideways drag to the card as a swipe.
      onPointerDown={(e) => {
        startX.current = e.clientX;
      }}
      onPointerUp={(e) => {
        if (startX.current === null) return;
        const dx = e.clientX - startX.current;
        startX.current = null;
        if (dx <= -SWIPE_DISTANCE) go(active + 1);
        else if (dx >= SWIPE_DISTANCE) go(active - 1);
      }}
      onPointerCancel={() => {
        startX.current = null;
      }}
      className={cn(
        'relative isolate flex touch-pan-y flex-col overflow-hidden rounded-card bg-panel text-on-panel select-none',
        className,
      )}
    >
      {/* Its first letter whole and a little cropped on the left, the rest run
          off the right edge: scenery, never read. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-10 -left-5 -z-10 font-display text-ghost-sm whitespace-nowrap text-brand-wash uppercase"
      >
        {items[active]!.title}
      </span>

      {/* Every photograph laid over the others and cross-faded, so the next
          one is already loaded when the card changes. A fixed height, the
          design's: an aspect ratio gives way to a tall photo's own height. */}
      <div className="relative h-48 shrink-0">
        {items.map(
          (item, i) =>
            item.image && (
              <img
                key={item.title}
                src={item.image.src}
                alt={i === active ? item.image.alt : ''}
                aria-hidden={i !== active}
                draggable={false}
                className={cn(
                  'absolute inset-0 size-full object-cover',
                  'transition-opacity duration-(--duration-base) ease-standard motion-reduce:transition-none',
                  i === active ? 'opacity-100' : 'opacity-0',
                )}
              />
            ),
        )}
      </div>

      <div className="flex flex-col gap-2 px-card pt-card pb-2">
        <div aria-live="polite" className="grid">
          {items.map((item, i) => (
            <div
              key={item.title}
              className={cn('col-start-1 row-start-1', i !== active && 'invisible')}
            >
              {/* Set as the About page's other in-section titles, the Overview
                  questions and the value names: same face, weight and size. */}
              <h3 className="m-0 font-sans text-xl font-bold tracking-tight text-brand-text">
                {item.title}
              </h3>
              <p className="m-0 mt-2 text-base leading-relaxed text-ink-secondary">{item.body}</p>
            </div>
          ))}
        </div>

        {/* A bar per statement, the chosen one long and in the brand green.
            Real buttons, for anyone who does not swipe; the bar is drawn
            inside a taller box so it is a finger's worth to hit. */}
        <div className="-ml-1 flex">
          {items.map((item, i) => (
            <button
              key={item.title}
              type="button"
              aria-label={`Show ${item.title.toLowerCase()}`}
              aria-pressed={i === active}
              onClick={() => go(i)}
              className={cn(
                'grid h-11 place-items-center px-1',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
              )}
            >
              <span
                className={cn(
                  'block h-1 rounded-full',
                  'transition-[width,background-color] duration-(--duration-base) ease-standard motion-reduce:transition-none',
                  i === active ? 'w-8 bg-brand-text' : 'w-4 bg-ink-muted/40',
                )}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
